import { createTheme } from '@mantine/core';

const mantineTheme = createTheme({
  primaryColor: 'iosBlue',
  colors: {
    // iOS System Colors
    iosBlue: [
      '#e6f2ff', // 0 - lightest
      '#cce5ff', // 1
      '#99cbff', // 2
      '#66b0ff', // 3
      '#3396ff', // 4
      '#007aff', // 5 - primary
      '#0066d4', // 6
      '#0052aa', // 7
      '#003d80', // 8
      '#002955', // 9 - darkest
    ],
    iosGreen: [
      '#e6faed', // 0
      '#ccf5db', // 1
      '#99ebbf', // 2
      '#66e0a3', // 3
      '#33d687', // 4
      '#00cc6a', // 5 - primary
      '#00a857', // 6
      '#008645', // 7
      '#006433', // 8
      '#004222', // 9
    ],
    iosRed: [
      '#ffebeb', // 0
      '#ffd6d6', // 1
      '#ffadad', // 2
      '#ff8585', // 3
      '#ff5c5c', // 4
      '#ff3b30', // 5 - primary
      '#d43128', // 6
      '#aa2720', // 7
      '#801d18', // 8
      '#551310', // 9
    ],
    iosOrange: [
      '#fff5e6', // 0
      '#ffebcc', // 1
      '#ffd699', // 2
      '#ffc266', // 3
      '#ffad33', // 4
      '#ff9500', // 5 - primary
      '#d47c00', // 6
      '#aa6300', // 7
      '#804a00', // 8
      '#553100', // 9
    ],
    iosPurple: [
      '#f5e6ff', // 0
      '#ebccff', // 1
      '#d699ff', // 2
      '#c266ff', // 3
      '#ad33ff', // 4
      '#af52de', // 5 - primary
      '#9144b8', // 6
      '#733692', // 7
      '#55286c', // 8
      '#371a46', // 9
    ],
    iosTeal: [
      '#e6f9ff', // 0
      '#ccf3ff', // 1
      '#99e7ff', // 2
      '#66dbff', // 3
      '#33cfff', // 4
      '#00c3ff', // 5 - primary
      '#00a2d4', // 6
      '#0081aa', // 7
      '#006080', // 8
      '#003f55', // 9
    ],
    iosPink: [
      '#ffe6f0', // 0
      '#ffcce0', // 1
      '#ff99c2', // 2
      '#ff66a3', // 3
      '#ff3385', // 4
      '#ff2d55', // 5 - primary
      '#d42546', // 6
      '#aa1d38', // 7
      '#801529', // 8
      '#550d1b', // 9
    ],
    iosIndigo: [
      '#ebebff', // 0
      '#d6d6ff', // 1
      '#adadff', // 2
      '#8585ff', // 3
      '#5c5cff', // 4
      '#5856d6', // 5 - primary
      '#4847b0', // 6
      '#38388a', // 7
      '#282964', // 8
      '#181a3e', // 9
    ],
    iosYellow: [
      '#fffbe6', // 0
      '#fff7cc', // 1
      '#ffef99', // 2
      '#ffe766', // 3
      '#ffdf33', // 4
      '#ffcc00', // 5 - primary
      '#d4a900', // 6
      '#aa8600', // 7
      '#806400', // 8
      '#554100', // 9
    ],
    iosGray: [
      '#f2f2f7', // 0 - systemGray6
      '#e5e5ea', // 1 - systemGray5
      '#d1d1d6', // 2 - systemGray4
      '#c7c7cc', // 3 - systemGray3
      '#aeaeb2', // 4 - systemGray2
      '#8e8e93', // 5 - systemGray
      '#75757a', // 6
      '#5c5c60', // 7
      '#434346', // 8
      '#2a2a2c', // 9
    ],
  },
  
  // Typography - iOS System Fonts
  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif',
  fontFamilyMonospace: 'SF Mono, Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
  
  headings: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Arial, sans-serif',
    fontWeight: '700',
    sizes: {
      h1: { fontSize: '34px', lineHeight: '1.2', fontWeight: '700' },
      h2: { fontSize: '28px', lineHeight: '1.25', fontWeight: '700' },
      h3: { fontSize: '22px', lineHeight: '1.3', fontWeight: '600' },
      h4: { fontSize: '20px', lineHeight: '1.35', fontWeight: '600' },
      h5: { fontSize: '17px', lineHeight: '1.4', fontWeight: '600' },
      h6: { fontSize: '15px', lineHeight: '1.5', fontWeight: '600' },
    },
  },
  
  // iOS-style shadows
  shadows: {
    xs: '0 1px 2px rgba(0, 0, 0, 0.04)',
    sm: '0 2px 4px rgba(0, 0, 0, 0.06)',
    md: '0 4px 8px rgba(0, 0, 0, 0.08)',
    lg: '0 8px 16px rgba(0, 0, 0, 0.1)',
    xl: '0 16px 32px rgba(0, 0, 0, 0.12)',
    // iOS-specific shadows
    iosCard: '0 4px 16px rgba(0, 0, 0, 0.08)',
    iosCardHover: '0 8px 24px rgba(0, 0, 0, 0.12)',
    iosModal: '0 20px 48px rgba(0, 0, 0, 0.2)',
  },
  
  // iOS-style radius
  radius: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
  },
  
  // Default props for components
  defaultRadius: 'md',
  defaultGradient: { from: 'iosBlue', to: 'iosPurple', deg: 135 },
  
  // Component-specific overrides
  components: {
    Paper: {
      defaultProps: {
        radius: 'lg',
        p: 'md',
      },
      styles: {
        root: {
          transition: 'box-shadow 250ms ease, transform 150ms ease',
        },
      },
    },
    Button: {
      defaultProps: {
        radius: 'md',
      },
      styles: {
        root: {
          fontWeight: 600,
          transition: 'all 150ms ease',
          '&:active': {
            transform: 'scale(0.96)',
          },
        },
      },
    },
    Card: {
      defaultProps: {
        radius: 'lg',
        shadow: 'iosCard',
      },
      styles: {
        root: {
          transition: 'box-shadow 250ms ease, transform 150ms ease',
          '&:hover': {
            boxShadow: 'var(--shadow-iosCardHover)',
          },
        },
      },
    },
    Modal: {
      defaultProps: {
        radius: 'lg',
        shadow: 'iosModal',
      },
      styles: {
        content: {
          overflow: 'hidden',
        },
      },
    },
    Input: {
      defaultProps: {
        radius: 'md',
      },
      styles: {
        input: {
          transition: 'all 150ms ease',
        },
      },
    },
    ActionIcon: {
      styles: {
        root: {
          transition: 'all 150ms ease',
          '&:active': {
            transform: 'scale(0.9)',
          },
        },
      },
    },
  },
  
  // Other configurations
  other: {
    setColors: {
      scarletviolet: 'linear-gradient(90deg, rgba(235, 21, 79, 0.8) 0%, rgba(138, 94, 159, 0.9) 100%)',
      swordshield: 'linear-gradient(90deg, rgba(93, 200, 242, 0.8) 0%, rgba(218, 131, 214, 0.9) 50%, rgba(246, 32, 83, 0.9) 100%)',
      sunmoon: 'linear-gradient(90deg, rgba(254, 200, 106, 0.8) 0%, rgba(125, 184, 228, 0.9) 100%)',
      xy: 'linear-gradient(90deg, rgba(0, 120, 175, 0.8) 0%, rgba(226, 27, 34, 0.9) 100%)',
      blackwhite: 'linear-gradient(90deg, rgba(17, 17, 17, 0.8) 0%, rgba(255, 255, 255, 0.9) 100%)',
      pop: 'linear-gradient(90deg, rgba(221, 17, 17, 0.8) 0%, rgba(255, 255, 255, 0.9) 100%)',
      platinum: 'linear-gradient(90deg, rgba(104, 104, 92, 0.8) 0%, rgba(193, 193, 181, 0.9) 100%)',
      diamondpearl: 'linear-gradient(90deg, rgba(66, 186, 255, 0.8) 0%, rgba(255, 166, 222, 0.9) 100%)',
      exteammagmarocketvsplasma: 'linear-gradient(90deg, rgba(255, 0, 0, 0.8) 0%, rgba(0, 0, 255, 0.9) 100%)',
      megaevolution: 'linear-gradient(90deg, rgba(218, 112, 214, 0.8) 0%, rgba(147, 112, 219, 0.85) 20%, rgba(100, 149, 237, 0.85) 40%, rgba(135, 206, 250, 0.85) 55%, rgba(64, 224, 208, 0.85) 70%, rgba(144, 238, 144, 0.85) 85%, rgba(255, 238, 88, 0.9) 100%)',
      mega: 'linear-gradient(90deg, rgba(218, 112, 214, 0.8) 0%, rgba(147, 112, 219, 0.85) 20%, rgba(100, 149, 237, 0.85) 40%, rgba(135, 206, 250, 0.85) 55%, rgba(64, 224, 208, 0.85) 70%, rgba(144, 238, 144, 0.85) 85%, rgba(255, 238, 88, 0.9) 100%)',
      promotional: 'linear-gradient(90deg, rgba(255, 215, 0, 0.8) 0%, rgba(255, 140, 0, 0.9) 100%)',
      other: 'linear-gradient(90deg, rgba(100, 100, 100, 0.8) 0%, rgba(150, 150, 150, 0.9) 100%)',
    },
    // Variant colors for price history chart
    variantColors: {
      normal: '#8e8e93',    // gray
      holo: '#00c3ff',      // cyan/teal
      reverseHolo: '#ff9500', // orange
      pokeballHolo: '#ff3b30', // red
      masterballHolo: '#af52de', // purple
    },
  } as any,
});

export default mantineTheme;
